import { Pipe, PipeTransform } from '@angular/core';
import { OnlineReservationCategory } from '@model/login-response.dto';
import { Reservation, Table } from '@model/reservation';
import { ReservationTag, ReservationTagCategory } from '@model/reservation-tag';
import { ReservationType } from '@model/tables-plan';
import { Common } from 'src/app/lib/common-methods';
import { ReservationService } from 'src/app/lib/reservation.service';

@Pipe({
  name: 'reservationsByTypeFilter',
})
export class ReservationsByTypeFilterPipe implements PipeTransform {

  constructor(private reservationService: ReservationService) {

  }

  transform(value: Reservation[], ...args: unknown[]): Reservation[] {

    const reservationType = args[0] as ReservationType;

    return value.filter(r => {
      const type = this.reservationService.statusToType(r.reservationDetails.reservationsStatus);

      return type === reservationType;
    });
  }

}

@Pipe({
  name: 'coversByReservationType',
})
export class CoversByReservationTypePipe implements PipeTransform {

  constructor(private reservationService: ReservationService) {

  }

  transform(value: Reservation[], ...args: unknown[]): string {

    const reservationType = args[0] as ReservationType;

    const reservations = value.filter(r => {
      const type = this.reservationService.statusToType(r.reservationDetails.reservationsStatus);

      return type === reservationType;
    });

    const covers = reservations.map(r => r.reservationDetails.covers).reduce((a, b) => a + b, 0);

    return `${Common.padStart(covers, 2)}`;
  }

}

@Pipe({
  name: 'allocatedTableNames',
})
export class AllocatedTableNamesPipe implements PipeTransform {

  transform(value: string, ...args: unknown[]): string {

    const maxTables = (args[0] ?? 1) as number;

    if (Common.isNullOrEmpty(value)) {
      return '';
    }

    const allocatedTables = value.split(',').filter(t => !Common.isNullOrEmpty(t));

    if (allocatedTables.length > maxTables) {
      return `${allocatedTables.slice(0, maxTables).join(',')}+`;
    }

    return allocatedTables.join(',');
  }

}

@Pipe({
  name: 'allocatedTableNamesWithCategory',
})
export class AllocatedTableNamesWithCategoryPipe implements PipeTransform {

  transform(value: string, category: OnlineReservationCategory | null | undefined, ...args: unknown[]): string {

    if (Common.isNullOrEmpty(value)) {
      return '';
    }

    if (category == null || Common.isNullOrEmpty(category.iconCaption)) {
      return value;
    }

    const allocatedTables = value.split(',').filter(t => !Common.isNullOrEmpty(t));

    let allocatedTablesWithCategory = allocatedTables.map(allocatedTable => {
      return `${allocatedTable} (${category?.iconCaption})`;
    });

    return allocatedTablesWithCategory.join(', ');
  }

}

@Pipe({
  name: 'uniqueCustomerTagsCategories',
})
export class UniqueCustomerTagsCategoriesPipe implements PipeTransform {

  transform(value: ReservationTag[] | undefined, ...args: unknown[]): string[] {

    const tagCategories = args[0] as ReservationTagCategory[];

    if (value == null || value.length <= 0 || tagCategories == null) {
      return [];
    }

    const uniqueCustomerTags = [...new Map(value.map(item => [item.categoryId, item])).values()];

    return uniqueCustomerTags.map(t => this.getTagColor(t, tagCategories)).filter(t => t != null) as string[];
  }

  getTagColor(tag: ReservationTag, tagCategories: ReservationTagCategory[]) {
    return tagCategories?.find(tagCategory => tagCategory.id === tag.categoryId)?.color;
  }

}

@Pipe({
  name: 'uniqueReservationTagsCategories',
})
export class UniqueReservationTagsCategoriesPipe implements PipeTransform {

  transform(value: ReservationTag[] | undefined, ...args: unknown[]): string[] {

    const tagCategories = args[0] as ReservationTagCategory[];

    if (value == null || value.length <= 0 || tagCategories == null) {
      return [];
    }

    const dietaryTagCategories = tagCategories.filter(tagCategory => tagCategory.isDietary());

    const uniqueReservationTags = [...new Map(value
      .filter(tag => !dietaryTagCategories?.some(tagCategory => tagCategory.id === tag.categoryId))
      .map(item => [item.categoryId, item])
    ).values()];

    return uniqueReservationTags.map(t => this.getTagColor(t, tagCategories)).filter(t => t != null) as string[];
  }

  getTagColor(tag: ReservationTag, tagCategories: ReservationTagCategory[]) {
    return tagCategories?.find(tagCategory => tagCategory.id === tag.categoryId)?.color;
  }

}
